import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { IconButton, Popper, Paper, MenuList, MenuItem, ClickAwayListener, CircularProgress } from '@material-ui/core';
import { VideoLibrary, PhotoLibrary, ArrowDropDown } from '@material-ui/icons';
import { ReactComponent as VideoUploadIcon } from 'Assets/video-upload.svg';
import ForNoImage from 'Assets/Placeholder.png';
import { v4 as uuid } from 'uuid';
import AXIOS from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveUploadInfo, saveUploadDetails, saveDummy, removeDummy } from 'store/store';
import { AppContext } from 'Context';
import { uploadLargeFile } from 'Utils/uploadWrapper';
import { getBucketName, generateS3Url } from 'Api/awsConfig';
import { axios, baseUrl } from 'Api';
import { Pathname } from 'Routes';
import { ALLOWED_UPLOADS_COUNT } from 'Commons';
import uploadManager from 'Utils/uploadManagerNew';
import CloudUploadModal from './CloudUploadModal';
import CloudPhotoAlbumModal from './CloudPhotoAlbumModal';
import styles from './UploadDropdown.module.css';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];

const UploadDropdown = ({
  onVideoUpload,
  onPhotoUpload,
  disabled = false,
  className = '',
  isCompact = false,
  isLoading = false,
  onCloudFilesSelected,
  showCloudOptions = false
}) => {
  const {
    userDetails,
    setUploadContainer,
    setUploadContainer2,
    setIamUploading,
    setDraftCategoryState,
    setAppSnackbar,
    base64ToBinary,
    setDownloadBox,
    setDownloadingFiles,
    autoSignIn,
    setTrailPopUpModalVisible,
    IsfreePlan,
    downloadAbortControllersRef,
    bulkDownloadCancelRef,
    // Cloud photo album modal state from context (persists across navigation)
    photoAlbumModalOpen,
    setPhotoAlbumModalOpen,
    pendingCloudPhotos,
    setPendingCloudPhotos,
    pendingCloudPhotoService,
    setPendingCloudPhotoService,
  } = useContext(AppContext);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = userDetails?.isSubscription?.user_id || localStorage.getItem('id');

  const uploadPercentages = useSelector((state) => state.upload);
  const updateUploadPercentages = (draft_id, data) => {
    dispatch(saveUploadInfo({ [draft_id]: data }));
  };
  const updateUploadDetails = (draft_id, data) => {
    dispatch(saveUploadDetails({ [draft_id]: data }));
  };

  const updateDummy = (dummy, status = 0) => {
    if (status === 1) {
      dispatch(saveDummy({ [dummy?.draft_id]: dummy }));
    } else {
      dispatch(removeDummy(dummy));
    }
  };

  // Helper functions
  function changeSize(size) {
    const newSize = size / 1024 / 1024;
    return newSize.toFixed(2);
  }

  // Helper functions for thumbnail generation
  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const convertBlobUrlToBase64 = async (blobUrl) => {
    try {
      const response = await fetch(blobUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch blob');
      }
      const blob = await response.blob();
      const base64 = await blobToBase64(blob);
      return base64;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error converting blob to base64:', error);
      throw error;
    }
  };

  const generateThumbnailsFromVideo = (file) => new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      // Return ForNoImage as fallback
      resolve({
        thumbnails: [ForNoImage],
        videoHeight: 0,
        videoWidth: 0,
        videoDuration: 0,
      });
    };

    video.onloadeddata = () => {
      const { duration, videoHeight, videoWidth } = video;

      const thumbnailCount = 6;
      const interval = duration / (thumbnailCount + 1);
      const tempThumbnails = [];

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let currentIndex = 1;

      const captureNextFrame = () => {
        if (currentIndex > thumbnailCount) {
          URL.revokeObjectURL(videoUrl);
          video.remove();

          if (tempThumbnails.length === 0) {
            resolve({
              thumbnails: [ForNoImage],
              videoHeight,
              videoWidth,
              videoDuration: duration,
            });
          } else {
            resolve({
              thumbnails: tempThumbnails,
              videoHeight,
              videoWidth,
              videoDuration: duration,
            });
          }
          return;
        }

        const time = interval * currentIndex;
        video.currentTime = time;

        video.onseeked = () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                tempThumbnails.push(url);
              }

              currentIndex += 1;
              captureNextFrame();
            },
            'image/jpeg',
            0.9
          );
        };
      };

      captureNextFrame();
    };
  });

  const addVideoToAPI = async (draftID, draftCategoryState) => {
    try {
      const apiUrl = `${baseUrl}/V6/addVideo`;
      const formData = new FormData();
      const draftData = draftCategoryState[draftID];
      if (draftData) {
        formData.append('draft_id', draftID || '');
        formData.append('title', draftData.title || '');
        formData.append('category_id', draftData.category || '');
        if (draftData.category_name) {
          formData.append('category_name', draftData.category_name);
        }
        formData.append('file_name', draftData.fileName || '');
        if (draftData.thumbnail) {
          const binaryThumbnail = base64ToBinary(draftData.thumbnail);
          formData.append('thumbnail', binaryThumbnail);
        }
        // Append each thumbnail as binary
        if (draftData.thumbnails && Array.isArray(draftData.thumbnails)) {
          draftData.thumbnails.forEach((binaryThumbnail) => {
            if (binaryThumbnail) {
              formData.append('thumbnails[]', binaryThumbnail);
            }
          });
        }
        formData.append('description', draftData.description || '');
        formData.append('video_size', changeSize(draftData.videoSize) || '');
        formData.append('group_ids', JSON.stringify(draftData.selectedGroups || []));
        formData.append('visibility', draftData.visibility || '');
        formData.append('subcategory_id', draftData.selectedSubCategory || '');
        formData.append('playlist_ids', draftData.selectedPlaylist || '');
        formData.append('videoHeight', draftData.videoHeight || '');
        formData.append('videoWidth', draftData.videoWidth || '');
        formData.append('videoOreintation', draftData.orientation || '');
        formData.append('s3_status', draftData.uploadStatus || '');
        formData.append('uploadKey', draftData.uploadKey || '');
        formData.append(
          'bucket_folder_path',
          `${draftData.s3Url}${draftData.uploadBucket}/${draftData.uploadKey}`
        );
        formData.append('s3_image_bucket_url', draftData.s3Url || '');
      }
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding video to API:', error.message || error);
      throw error;
    }
  };

  const [open, setOpen] = useState(false);
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [selectedCloudService, setSelectedCloudService] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const anchorRef = useRef(null);

  // Photo album modal state - now using context (photoAlbumModalOpen, pendingCloudPhotos, pendingCloudPhotoService)
  // to persist across navigation

  // Track completed cloud uploads for cancel toast
  const cloudUploadCompletedCountRef = useRef(0);
  const cloudUploadTotalCountRef = useRef(0);
  const isCloudUploadSessionActiveRef = useRef(false);

  // Check if we should auto-open cloud modal after OAuth
  useEffect(() => {
    const autoOpenService = sessionStorage.getItem('autoOpenCloudModal');
    if (autoOpenService) {
      sessionStorage.removeItem('autoOpenCloudModal');
      setSelectedCloudService(autoOpenService);
      setCloudModalOpen(true);
    }
  }, []);

  // Helper to show cloud upload cancel toast (only once per session)
  const showCloudUploadCancelToast = useCallback(() => {
    if (isCloudUploadSessionActiveRef.current) {
      const completedCount = cloudUploadCompletedCountRef.current;
      setAppSnackbar({
        isVisible: true,
        type: 'success',
        message: `Upload cancelled. ${completedCount} ${completedCount === 1 ? 'item' : 'items'} uploaded`,
      });
      // Reset session flag to prevent duplicate toasts
      isCloudUploadSessionActiveRef.current = false;
    }
  }, [setAppSnackbar]);

  const handleToggle = () => {
    if (!disabled) {
      setOpen((prevOpen) => !prevOpen);
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleVideoUpload = (event) => {
    handleClose(event);
    if (onVideoUpload) {
      onVideoUpload();
    }
  };

  const handlePhotoUpload = (event) => {
    handleClose(event);
    if (onPhotoUpload) {
      onPhotoUpload();
    }
  };

  const handleCloudUpload = (service) => async (event) => {
    handleClose(event);

    // Check upload limit before opening cloud modal
    try {
      const res = await autoSignIn();
      const currentlyUploadingCount = uploadManager.getCurrentlyUploadingCount();

      // Check if user has reached upload limit
      if (
        IsfreePlan
        && !res?.data?.subscription
        && res?.data?.totalContentUploaded + currentlyUploadingCount >= ALLOWED_UPLOADS_COUNT
      ) {
        // Show limit modal instead of cloud browser
        setTrailPopUpModalVisible(true);
        return;
      }

      // If limit not reached, open cloud browser
      setSelectedCloudService(service);
      setCloudModalOpen(true);
    } catch (error) {
      console.error('Error checking upload limits:', error);
      // Still open cloud browser if check fails
      setSelectedCloudService(service);
      setCloudModalOpen(true);
    }
  };

  const handleCloudModalClose = () => {
    setCloudModalOpen(false);
    setSelectedCloudService(null);
  };

  const handleCloudFilesSelected = async (files, service) => {
    if (files.length === 0) return;

    // Separate videos and photos based on fileType property
    const videoFiles = files.filter((file) => file.fileType === 'video');
    const photoFiles = files.filter((file) => file.fileType === 'image');

    // eslint-disable-next-line no-console
    console.log('[CLOUD_UPLOAD] Files separated:', { videos: videoFiles.length, photos: photoFiles.length });

    // If there are photos, store them and show album selection modal
    if (photoFiles.length > 0) {
      setPendingCloudPhotos(photoFiles);
      setPendingCloudPhotoService(service);
      setPhotoAlbumModalOpen(true);
    }

    // If no videos, just handle photos (modal will process them)
    if (videoFiles.length === 0) {
      handleCloudModalClose();
      return;
    }

    // Process videos through the existing flow
    setIsUploading(true);
    const filesToProcess = videoFiles;

    // Reset cancel flag for new cloud download session
    if (bulkDownloadCancelRef) {
      bulkDownloadCancelRef.current = false;
    }

    // Reset cloud upload tracking for this session
    cloudUploadCompletedCountRef.current = 0;
    cloudUploadTotalCountRef.current = filesToProcess.length;
    isCloudUploadSessionActiveRef.current = true;

    // Initialize download progress box - mark first 2 files as downloading, rest as queued
    const MAX_CONCURRENT_DOWNLOADS = 2;
    setDownloadBox(true);
    const initialDownloadingFiles = filesToProcess.map((file, index) => ({
      id: file.id,
      name: file.name,
      progress: 0,
      status: index < MAX_CONCURRENT_DOWNLOADS ? 'downloading' : 'queued',
      source: 'cloud', // Mark as cloud download for identification
    }));
    setDownloadingFiles(initialDownloadingFiles);

    try {
      // Get user's cloud service access token from localStorage
      const cloudServices = localStorage.getItem('cloudConnectedServices');
      const cloudAccessToken = cloudServices ? JSON.parse(cloudServices)[service]?.accessToken : null;

      if (!cloudAccessToken) {
        throw new Error('Cloud service not connected');
      }

      // Process each file: download from cloud and upload to S3
      // Downloads happen sequentially (one at a time), uploads are queued by uploadManager
      const processFile = async (file) => {
        // Check if cancelled before starting
        if (bulkDownloadCancelRef?.current) {
          setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'cancelled' } : f)));
          showCloudUploadCancelToast();
          return { success: false, fileName: file.name, cancelled: true };
        }

        // Create AbortController for this download
        const controller = new AbortController();
        if (downloadAbortControllersRef) {
          downloadAbortControllersRef.current.set(file.id, controller);
        }

        // Create draft ID early for download tracking
        const draftID = uuid();
        const videoTitle = file.name.replace(/\.[^/.]+$/, '');

        try {
          // Create dummy entry to show download in upload panel
          const downloadDummy = {
            draft_id: draftID,
            title: videoTitle,
            fileName: file.name,
            thumbnail: ForNoImage, // Use placeholder until actual thumbnail is generated
            category: `${service === 'dropbox' ? 'Dropbox' : 'Google Drive'}`,
            video_description: '',
            video_size: changeSize(file.size),
            type: 0, // Content type (0 = video)
            group_id: [],
            content_status: 'Video',
            uploadIsTrying: 1, // Flag indicating upload in progress
          };

          // Add to upload panel before download starts
          updateDummy(downloadDummy, 1);
          updateUploadPercentages(draftID, 'Downloading...');

          let tempUrl = null;
          let headers = {};

          if (service === 'dropbox') {
            // Get Dropbox temporary link
            const dropboxHeaders = {
              Authorization: `Bearer ${cloudAccessToken}`,
              'Content-Type': 'application/json',
            };

            // Add namespace header for shared folder access
            if (file.namespaceId) {
              dropboxHeaders['Dropbox-API-Path-Root'] = JSON.stringify({
                '.tag': 'namespace_id',
                namespace_id: file.namespaceId,
              });
            }

            const response = await AXIOS.post(
              'https://api.dropboxapi.com/2/files/get_temporary_link',
              { path: file.id },
              { headers: dropboxHeaders },
            );
            tempUrl = response.data.link;
          } else if (service === 'googledrive') {
            // Google Drive direct download link
            tempUrl = `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`;
            headers = {
              Authorization: `Bearer ${cloudAccessToken}`,
            };
          }

          // Download file with progress tracking and abort signal
          const downloadResponse = await fetch(tempUrl, { headers, signal: controller.signal });

          if (!downloadResponse.ok) {
            throw new Error(`Failed to download ${file.name}`);
          }

          const contentLength = downloadResponse.headers.get('content-length');
          // Use content-length header, or file.size from cloud API, or 0 if unknown
          const totalBytes = contentLength ? parseInt(contentLength, 10) : (file.size || 0);

          // Read response body with progress tracking
          const reader = downloadResponse.body.getReader();
          const chunks = [];
          let receivedBytes = 0;
          let lastProgressUpdate = 0;
          let lastSpeedUpdate = 0;
          let lastBytesForSpeed = 0;
          let currentSpeed = 0;

          // eslint-disable-next-line no-constant-condition
          while (true) {
            // Check if cancelled during download
            if (bulkDownloadCancelRef?.current || controller.signal.aborted) {
              reader.cancel();
              throw new Error('Download cancelled');
            }

            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();

            if (done) break;

            chunks.push(value);
            receivedBytes += value.length;

            // Throttle progress updates to every 100ms to avoid too many state updates
            const now = Date.now();
            if (now - lastProgressUpdate > 100) {
              lastProgressUpdate = now;

              // Calculate speed every 500ms for stable display
              if (now - lastSpeedUpdate > 500) {
                const bytesDownloaded = receivedBytes - lastBytesForSpeed;
                const timeDiff = (now - lastSpeedUpdate) / 1000; // seconds
                currentSpeed = timeDiff > 0 ? bytesDownloaded / timeDiff : 0;
                lastSpeedUpdate = now;
                lastBytesForSpeed = receivedBytes;
              }

              // Calculate progress and ETA
              const currentReceivedBytes = receivedBytes;
              const speedSnapshot = currentSpeed;
              let downloadProgress;
              let etaValue = 0;

              if (totalBytes > 0) {
                downloadProgress = Math.round((currentReceivedBytes / totalBytes) * 100);
                // Calculate ETA
                if (speedSnapshot > 0) {
                  const remainingBytes = totalBytes - currentReceivedBytes;
                  etaValue = Math.round(remainingBytes / speedSnapshot);
                }
              } else {
                downloadProgress = 0; // Show 0% when size unknown, UI will show speed/eta
              }

              updateUploadPercentages(draftID, `Downloading: ${downloadProgress}%`);

              // Update download progress box with speed and eta
              setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? {
                ...f,
                progress: downloadProgress,
                receivedBytes: currentReceivedBytes,
                speed: speedSnapshot,
                eta: etaValue,
              } : f)));
            }
          }

          // Combine chunks into blob
          const blob = new Blob(chunks);

          // Mark download as completed in progress box
          setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, progress: 100, status: 'completed' } : f)));

          // Remove download dummy - upload will create its own entry
          updateDummy(downloadDummy, 0);

          // Create a File object from blob (like local file upload)
          const fileObject = new File([blob], file.name, {
            type: file.mimeType || blob.type || 'application/octet-stream',
          });

          // Show thumbnail generation status
          updateUploadPercentages(draftID, 'Generating thumbnails...');

          // Generate thumbnails from video
          let thumbnailData;
          try {
            thumbnailData = await generateThumbnailsFromVideo(fileObject);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error generating thumbnails:', error);
            // Use fallback
            thumbnailData = {
              thumbnails: [ForNoImage],
              videoHeight: 0,
              videoWidth: 0,
              videoDuration: 0,
            };
          }

          const { thumbnails, videoHeight, videoWidth } = thumbnailData;

          // Get main thumbnail (first one)
          let thumbnailBase64 = '';
          const selectedThumbnailSrc = thumbnails[0];

          if (selectedThumbnailSrc === ForNoImage) {
            // Convert ForNoImage to base64
            const response = await fetch(ForNoImage);
            const thumbnailBlob = await response.blob();
            thumbnailBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(thumbnailBlob);
            });
          } else if (selectedThumbnailSrc && selectedThumbnailSrc.startsWith('blob:')) {
            try {
              thumbnailBase64 = await convertBlobUrlToBase64(selectedThumbnailSrc);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error converting thumbnail:', error);
              // Fallback to ForNoImage
              const response = await fetch(ForNoImage);
              const thumbnailBlob = await response.blob();
              thumbnailBase64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(thumbnailBlob);
              });
            }
          }

          // Convert all thumbnails to binary
          const thumbnailsBinary = await Promise.all(
            thumbnails.map(async (thumbnail) => {
              try {
                if (thumbnail === ForNoImage) {
                  const response = await fetch(ForNoImage);
                  const thumbnailBlob = await response.blob();
                  const base64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsDataURL(thumbnailBlob);
                  });
                  return base64ToBinary(base64);
                }

                if (!thumbnail) {
                  return null;
                }
                const base64 = await convertBlobUrlToBase64(thumbnail);
                return base64ToBinary(base64);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error converting thumbnail:', error);
                return null;
              }
            })
          );

          // Generate unique filename and get S3 config (same as VideoUploadModal)
          const fileExtension = fileObject.name.split('.').pop().toLowerCase();
          const number = Math.floor(Math.random() * 10000) + 1;
          const fileName = `${Date.now()}-${userId}-video-${number}.${fileExtension}`;
          const bucketName = getBucketName(userId);
          const s3Url = generateS3Url();

          // Upload to S3 immediately after download (uploadLargeFile handles all tracking)
          uploadLargeFile({
            bucket: bucketName,
            key: fileName,
            file: fileObject,
            draftId: draftID,
            uploadPercentages,
            updateUploadPercentages,
            setUploadContainer,
            setUploadContainer2,
            setIamUploading,
            dummyData: {
              title: videoTitle,
              fileName,
              thumbnail: thumbnailBase64,
              thumbnails: thumbnailsBinary,
              ForNoImage,
              category: '',
              videoSize: fileObject.size,
              changeSize,
            },
            updateDummy, // Use actual updateDummy function to show in upload panel
            setDraftCategoryState,
            onSuccess: async (draft_id) => {
              // Increment completed cloud upload count
              cloudUploadCompletedCountRef.current += 1;

              // Save minimal draft state for API call
              const draftData = {
                title: videoTitle,
                // category_name: file.folder_name || '', // Pass folder name to category_name for addVideo API
                category_name: 'All Videos',
                fileName,
                thumbnail: thumbnailBase64,
                thumbnails: thumbnailsBinary,
                description: '',
                videoSize: fileObject.size,
                selectedGroups: [],
                visibility: '2', // All Viewers
                selectedSubCategory: '',
                selectedPlaylist: '',
                videoHeight,
                videoWidth,
                orientation: videoHeight > videoWidth ? 2 : 1,
                uploadStatus: 'completed',
                uploadKey: fileName,
                uploadBucket: bucketName,
                s3Url,
              };

              setDraftCategoryState((prevState) => ({
                ...prevState,
                [draft_id]: draftData,
              }));

              // Create a temporary draftCategoryState object for the API call
              const tempDraftState = {
                [draft_id]: draftData,
              };

              // Call API to save video metadata
              try {
                await addVideoToAPI(draft_id, tempDraftState);
                // setAppSnackbar({
                //   isVisible: true,
                //   message: `Successfully uploaded ${file.name} from ${service === 'dropbox' ? 'Dropbox' : 'Google Drive'}`,
                //   type: 'success',
                // });
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to add video to API:', error);
                setAppSnackbar({
                  isVisible: true,
                  message: `Uploaded ${file.name} but failed to save metadata`,
                  type: 'warning',
                });
              }
            },
            onError: (error) => {
              // eslint-disable-next-line no-console
              console.error('Upload failed for:', file.name, error);
              setAppSnackbar({
                isVisible: true,
                message: `Failed to upload ${file.name}`,
                type: 'error',
              });
            },
            updateUploadDetails,
          });

          // Clean up abort controller on success
          if (downloadAbortControllersRef) {
            downloadAbortControllersRef.current.delete(file.id);
          }

          return { success: true, fileName: file.name };
        } catch (error) {
          // Clean up abort controller
          if (downloadAbortControllersRef) {
            downloadAbortControllersRef.current.delete(file.id);
          }

          // Check if this was a cancellation
          const isCancelled = error.name === 'AbortError' || error.message === 'Download cancelled' || bulkDownloadCancelRef?.current;

          if (isCancelled) {
            // Mark as cancelled, don't show error
            setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'cancelled' } : f)));
            updateUploadPercentages(draftID, null);
            updateDummy({ draft_id: draftID }, 0);
            showCloudUploadCancelToast();
            return { success: false, fileName: file.name, cancelled: true };
          }

          // eslint-disable-next-line no-console
          console.error(`Error processing ${file.name}:`, error);

          // Mark download as error in progress box
          setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'error' } : f)));

          // Clean up dummy entry on error
          updateUploadPercentages(draftID, null);
          updateDummy({ draft_id: draftID }, 0);

          setAppSnackbar({
            isVisible: true,
            message: `Failed to download ${file.name}: ${error.message}`,
            type: 'error',
          });
          return { success: false, fileName: file.name, error: error.message };
        }
      };

      // Process downloads with concurrency of 2 (like "All videos download" feature)
      // When one download completes, the next queued item takes its place
      // Uploads are handled by uploadManager which has its own queue
      const MAX_CONCURRENT_DOWNLOADS_LIMIT = 2;
      const downloadState = { currentIndex: 0, activeDownloads: 0 };

      const startNextDownload = () => {
        // Check if cancelled - don't start new downloads
        if (bulkDownloadCancelRef?.current) {
          // Mark remaining queued files as cancelled
          setDownloadingFiles((prev) => prev.map((f) => (f.status === 'queued' ? { ...f, status: 'cancelled' } : f)));
          // Show cancel toast with completed upload count
          showCloudUploadCancelToast();
          return;
        }

        // Check if there are more files to process and we have capacity
        if (downloadState.currentIndex >= filesToProcess.length || downloadState.activeDownloads >= MAX_CONCURRENT_DOWNLOADS_LIMIT) {
          return;
        }

        const file = filesToProcess[downloadState.currentIndex];
        downloadState.currentIndex += 1;
        downloadState.activeDownloads += 1;

        // Update status to show current file is downloading
        setDownloadingFiles((prev) => prev.map((f) => {
          if (f.id === file.id) {
            return { ...f, status: 'downloading', progress: 0 };
          }
          return f;
        }));

        // Process file and when done, start next one
        processFile(file).finally(() => {
          downloadState.activeDownloads -= 1;
          // Start next file when this one completes (if not cancelled)
          if (!bulkDownloadCancelRef?.current) {
            startNextDownload();
          }
        });

        // Try to start another download if we have capacity
        startNextDownload();
      };

      // Start initial batch of downloads (up to MAX_CONCURRENT_DOWNLOADS_LIMIT)
      startNextDownload();

      // Close modal and navigate to your videos immediately (like VideoUploadModal)
      handleCloudModalClose();
      navigate(Pathname.yourVideos);

      // Call the parent callback if provided (only for videos)
      if (onCloudFilesSelected && filesToProcess.length > 0) {
        onCloudFilesSelected(filesToProcess, service);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading cloud files:', error);

      setAppSnackbar({
        isVisible: true,
        message: error.message || 'Failed to upload files from cloud storage',
        type: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle photo album selection - download photos and upload to album
  const handlePhotoAlbumSelected = async (albumData) => {
    const { id: albumId, title: albumTitle } = albumData;
    const photos = pendingCloudPhotos;
    const service = pendingCloudPhotoService;

    // Close the album modal
    setPhotoAlbumModalOpen(false);

    if (photos.length === 0) {
      setPendingCloudPhotos([]);
      setPendingCloudPhotoService(null);
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[CLOUD_PHOTO_UPLOAD] Starting photo upload to album:', albumId, 'photos:', photos.length);

    // Get cloud access token
    const cloudServices = localStorage.getItem('cloudConnectedServices');
    const cloudAccessToken = cloudServices ? JSON.parse(cloudServices)[service]?.accessToken : null;

    if (!cloudAccessToken) {
      setAppSnackbar({
        isVisible: true,
        message: 'Cloud service not connected',
        type: 'error',
      });
      setPendingCloudPhotos([]);
      setPendingCloudPhotoService(null);
      return;
    }

    // Show download progress box
    setDownloadBox(true);
    const initialDownloadingFiles = photos.map((file, index) => ({
      id: file.id,
      name: file.name,
      progress: 0,
      status: index < 2 ? 'downloading' : 'queued',
      source: 'cloud-photo',
    }));
    setDownloadingFiles(initialDownloadingFiles);

    // Register photo upload with upload manager
    uploadManager.createPhotoUpload({
      albumId,
      albumTitle: albumTitle || 'Photo Album',
      photoCount: photos.length,
      updateUploadPercentages,
    });

    // Initialize upload percentage
    updateUploadPercentages(albumId, `0/${photos.length}`);

    // Navigate to photo albums
    navigate(`${Pathname.yourVideos}?tab=photoalbum`);

    // Process photos: download from cloud, then batch upload to album
    const downloadedPhotos = [];
    let downloadedCount = 0;

    // Download photos with concurrency of 2
    const downloadPhoto = async (photo, index) => {
      try {
        // Update status to downloading
        setDownloadingFiles((prev) => prev.map((f) => (f.id === photo.id ? { ...f, status: 'downloading' } : f)));

        let tempUrl = null;
        let headers = {};

        if (service === 'dropbox') {
          // Get Dropbox temporary link - use ID for Dropbox
          const dropboxHeaders = {
            Authorization: `Bearer ${cloudAccessToken}`,
            'Content-Type': 'application/json',
          };

          // Add namespace header for shared folder access
          if (photo.namespaceId) {
            dropboxHeaders['Dropbox-API-Path-Root'] = JSON.stringify({
              '.tag': 'namespace_id',
              namespace_id: photo.namespaceId,
            });
          }

          const response = await AXIOS.post(
            'https://api.dropboxapi.com/2/files/get_temporary_link',
            { path: photo.id },
            { headers: dropboxHeaders },
          );
          tempUrl = response.data.link;
        } else if (service === 'googledrive') {
          // Google Drive direct download link
          tempUrl = `https://www.googleapis.com/drive/v3/files/${photo.id}?alt=media`;
          headers = {
            Authorization: `Bearer ${cloudAccessToken}`,
          };
        }

        // Download the photo
        const downloadResponse = await fetch(tempUrl, { headers });
        if (!downloadResponse.ok) {
          throw new Error(`Failed to download ${photo.name}`);
        }

        const blob = await downloadResponse.blob();

        // Mark as completed
        setDownloadingFiles((prev) => prev.map((f) => (f.id === photo.id ? { ...f, progress: 100, status: 'completed' } : f)));

        downloadedCount += 1;
        return { blob, name: photo.name, index };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error downloading photo ${photo.name}:`, error);
        setDownloadingFiles((prev) => prev.map((f) => (f.id === photo.id ? { ...f, status: 'error' } : f)));
        return null;
      }
    };

    // Download all photos with concurrency
    const downloadQueue = [...photos];
    const activeDownloads = new Set();
    const MAX_CONCURRENT = 2;

    await new Promise((resolve) => {
      const processNext = async () => {
        while (activeDownloads.size < MAX_CONCURRENT && downloadQueue.length > 0) {
          const photo = downloadQueue.shift();
          const photoIndex = photos.indexOf(photo);
          activeDownloads.add(photo.id);

          downloadPhoto(photo, photoIndex).then((result) => {
            activeDownloads.delete(photo.id);
            if (result) {
              downloadedPhotos.push(result);
            }
            if (downloadQueue.length > 0 || activeDownloads.size > 0) {
              processNext();
            } else {
              resolve();
            }
          });
        }

        if (downloadQueue.length === 0 && activeDownloads.size === 0) {
          resolve();
        }
      };

      processNext();
    });

    // eslint-disable-next-line no-console
    console.log('[CLOUD_PHOTO_UPLOAD] Downloaded:', downloadedPhotos.length, 'photos');

    // Now upload photos in batches to the album
    if (downloadedPhotos.length > 0) {
      const batchSize = 5;
      let uploadedCount = 0;
      const getToken = () => localStorage.getItem('token');

      // Sort by original index to maintain order
      downloadedPhotos.sort((a, b) => a.index - b.index);

      // Convert first photo to base64 for album icon (always send in first batch)
      let iconBase64 = null;
      if (downloadedPhotos.length > 0) {
        const firstBlob = downloadedPhotos[0].blob;
        iconBase64 = await new Promise((resolveBase64) => {
          const reader = new FileReader();
          reader.onloadend = () => resolveBase64(reader.result);
          reader.readAsDataURL(firstBlob);
        });
      }

      // Process in batches
      for (let i = 0; i < downloadedPhotos.length; i += batchSize) {
        const batch = downloadedPhotos.slice(i, i + batchSize);
        const isFirstBatch = i === 0;

        try {
          const formData = new FormData();
          formData.append('album_id', albumId);
          formData.append('visibility', '2'); // All Viewers
          formData.append('group_id', '');
          // Send icon only in the first batch for new albums
          if (isFirstBatch && iconBase64) {
            formData.append('icon', iconBase64);
          }
          formData.append('token', getToken());

          // Append photos
          batch.forEach(({ blob, name }, idx) => {
            formData.append('photo_file[]', blob, name || `photo_${idx}.jpg`);
          });

          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${baseUrl}/addPhotoContent`, formData);

          uploadedCount += batch.length;

          // Update progress
          updateUploadPercentages(albumId, `${uploadedCount}/${downloadedPhotos.length}`);
          uploadManager.updatePhotoUploadProgress(albumId, uploadedCount, downloadedPhotos.length);

          // eslint-disable-next-line no-console
          console.log(`[CLOUD_PHOTO_UPLOAD] Batch uploaded: ${uploadedCount}/${downloadedPhotos.length}`);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[CLOUD_PHOTO_UPLOAD] Batch upload error:', error);
        }
      }

      // Complete
      updateUploadPercentages(albumId, null);
      uploadManager.completePhotoUpload(albumId);

      setAppSnackbar({
        isVisible: true,
        message: `Successfully uploaded ${uploadedCount} photos to "${albumTitle}"`,
        type: 'success',
      });
    }

    // Clean up
    setPendingCloudPhotos([]);
    setPendingCloudPhotoService(null);
  };

  const handleListKeyDown = useCallback((event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }, []);

  // Return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  if (isCompact) {
    return (
      <div className={styles.compactContainer}>
        <button
          ref={anchorRef}
          className={`${styles.compactButton} ${className} ${disabled ? styles.disabled : ''}`}
          onClick={handleToggle}
          disabled={disabled}
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
        >
          <VideoUploadIcon className={styles.uploadIcon} />
          {isLoading && <CircularProgress size={14} style={{ marginLeft: '4px', color: '#fff' }} />}
          <ArrowDropDown className={styles.dropdownIcon} />
        </button>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          className={styles.popper}
        >
          <Paper className={styles.paper}>
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList
                autoFocusItem={open}
                id="composition-menu"
                aria-labelledby="composition-button"
                onKeyDown={handleListKeyDown}
                className={styles.menuList}
              >
                <MenuItem onClick={handleVideoUpload} className={styles.menuItem}>
                  <VideoLibrary className={styles.menuIcon} />
                  <span className={styles.menuText}>Upload Videos</span>
                </MenuItem>
                <MenuItem onClick={handlePhotoUpload} className={styles.menuItem}>
                  <PhotoLibrary className={styles.menuIcon} />
                  <span className={styles.menuText}>Upload Photos</span>
                </MenuItem>
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <button
        ref={anchorRef}
        className={`${styles.uploadButton} ${className} ${disabled ? styles.disabled : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
      >
        {/* <VideoUploadIcon className={styles.uploadIcon} /> */}
        <span className={styles.buttonText}>Upload</span>
        {isLoading && <CircularProgress size={16} style={{ marginLeft: '8px', color: '#fff' }} />}
        {/* <ArrowDropDown className={styles.dropdownIcon} /> */}
      </button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        className={styles.popper}
      >
        <Paper className={styles.paper}>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList
              autoFocusItem={open}
              id="composition-menu"
              aria-labelledby="composition-button"
              onKeyDown={handleListKeyDown}
              className={styles.menuList}
            >
              <MenuItem onClick={handleVideoUpload} className={styles.menuItem}>
                <VideoLibrary className={styles.menuIcon} />
                <span className={styles.menuText}>Upload Videos</span>
              </MenuItem>
              <MenuItem onClick={handlePhotoUpload} className={styles.menuItem}>
                <PhotoLibrary className={styles.menuIcon} />
                <span className={styles.menuText}>Upload Photos</span>
              </MenuItem>
              {showCloudOptions && (
                <>
                  <MenuItem onClick={handleCloudUpload('dropbox')} className={styles.menuItem}>
                    <svg
                      className={styles.menuIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      style={{ color: '#333' }}
                    >
                      <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z" />
                    </svg>
                    <span className={styles.menuText}>From Dropbox</span>
                  </MenuItem>
                  <MenuItem onClick={handleCloudUpload('googledrive')} className={styles.menuItem}>
                    <svg
                      className={styles.menuIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 87.3 78"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                    >
                      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" />
                      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" />
                      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" />
                      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" />
                      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" />
                      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" />
                    </svg>
                    <span className={styles.menuText}>From Google Drive</span>
                  </MenuItem>
                </>
              )}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>

      <CloudUploadModal
        open={cloudModalOpen}
        onClose={handleCloudModalClose}
        service={selectedCloudService}
        onFilesSelected={handleCloudFilesSelected}
        isUploading={isUploading}
        onShowMessage={(message) => setAppSnackbar({
          isVisible: true,
          message,
          type: 'default',
        })}
      />

      <CloudPhotoAlbumModal
        open={photoAlbumModalOpen}
        onClose={() => {
          setPhotoAlbumModalOpen(false);
          setPendingCloudPhotos([]);
          setPendingCloudPhotoService(null);
        }}
        onAlbumSelected={handlePhotoAlbumSelected}
        photoCount={pendingCloudPhotos.length}
      />
    </div>
  );
};

export default UploadDropdown;
